import React, { useRef, useEffect, useState } from 'react';
import { TextField, Box, CircularProgress } from '@mui/material';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';

interface AddressComponents {
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
}

interface AustralianAddressAutocompleteProps {
  value: string;
  onChange: (address: AddressComponents) => void;
  onInputChange?: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

export const AustralianAddressAutocomplete: React.FC<AustralianAddressAutocompleteProps> = ({
  value,
  onChange,
  onInputChange,
  label = 'Street Address',
  required = false,
  disabled = false,
  error = false,
  helperText,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputHandlerRef = useRef<(e: Event) => void>();
  // Use refs to preserve user's typed value - persists across renders and can't be overwritten by Google
  const userTypedValueRef = useRef<string>('');
  const lastStreetNumberWithSuffixRef = useRef<string>('');
  const capturedStreetWithSuffixRef = useRef<string>('');

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      console.warn('Google Maps API key not found. Address autocomplete will not work.');
      return undefined;
    }

    if (!inputRef.current) return undefined;

    const initAutocomplete = () => {
      if (!inputRef.current) return;

      const autocompleteInstance = new google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'AU' }, // Restrict to Australia
        fields: ['address_components', 'formatted_address', 'geometry', 'place_id'],
      });

      // IMPORTANT: Store user's typed value BEFORE place_changed fires
      // We need to capture it when user is typing, not when they select
      
      // Listen to input events to capture what user types before Google modifies it
      const inputElement = inputRef.current;
      if (inputElement) {
        const inputHandler = (e: Event) => {
          const target = e.target as HTMLInputElement;
          const typedValue = target.value || '';
          
          // Always store what user types
          userTypedValueRef.current = typedValue;
          
          // Extract everything before the first comma (the street part)
          const parts = typedValue.split(',');
          const streetPart = parts[0]?.trim() || '';
          
          // Check if user typed a street with suffix (e.g., "34a Raimonde Road")
          // Check if it matches pattern like "34a Raimonde Road" or "34a Raimonde Rd"
          const streetWithSuffixMatch = streetPart.match(/^(\d+[a-zA-Z]+\s+[^,]+)/);
          if (streetWithSuffixMatch) {
            capturedStreetWithSuffixRef.current = streetPart; // Store the full street part
            const suffixMatch = streetPart.match(/^(\d+)([a-zA-Z]+)\s/);
            if (suffixMatch) {
              lastStreetNumberWithSuffixRef.current = suffixMatch[1] + suffixMatch[2]; // Store "34a"
            }
          } else if (streetPart && /\d/.test(streetPart)) {
            // Also capture full street addresses without suffixes (e.g., "7 Havilah PL")
            // This helps preserve complete route names that Google might truncate
            // Only capture if it looks like a complete street address (has a number and some text)
            if (streetPart.length > 5 && /\d+\s+[A-Za-z]/.test(streetPart)) {
              capturedStreetWithSuffixRef.current = streetPart;
            }
          }
        };
        
        inputElement.addEventListener('input', inputHandler);
        inputHandlerRef.current = inputHandler;
      }

      autocompleteInstance.addListener('place_changed', () => {
        const place = autocompleteInstance.getPlace();
        
        if (!place.address_components) {
          return;
        }

        // CRITICAL: Get the input value IMMEDIATELY - Google might have modified it
        // Check if input still has the suffix before Google processes it
        const inputValueAtPlaceChanged = inputRef.current?.value || '';
        
        // IMPORTANT: Get the actual input value that Google Maps Autocomplete set
        const actualInputValue = inputRef.current?.value || '';
        
        // CRITICAL: Extract the street address from user's typed value BEFORE Google modified it
        // This is the most reliable way to preserve suffixes like "a" in "34a"
        let preservedStreet = '';
        let preservedSuffix = '';
        let userStreetNumber = '';
        
        // First priority: Check input value right now - it might still have "34a" before Google processes
        if (inputValueAtPlaceChanged) {
          const inputParts = inputValueAtPlaceChanged.split(',');
          const inputStreetPart = inputParts[0]?.trim() || '';
          const suffixPattern = inputStreetPart.match(/^(\d+)([a-zA-Z]+)\s/);
          if (suffixPattern) {
            preservedStreet = inputStreetPart;
            userStreetNumber = suffixPattern[1];
            preservedSuffix = suffixPattern[2];
          }
        }
        
        // Second priority: Check if we captured the street with suffix from input handler
        if (!preservedStreet && capturedStreetWithSuffixRef.current) {
          preservedStreet = capturedStreetWithSuffixRef.current;
          const suffixPattern = capturedStreetWithSuffixRef.current.match(/^(\d+)([a-zA-Z]+)\s/);
          if (suffixPattern) {
            userStreetNumber = suffixPattern[1];
            preservedSuffix = suffixPattern[2];
          }
        }
        
        // Second, check the last stored street number with suffix
        if (!preservedSuffix && lastStreetNumberWithSuffixRef.current) {
          const suffixMatch = lastStreetNumberWithSuffixRef.current.match(/^(\d+)([a-zA-Z]+)$/);
          if (suffixMatch) {
            userStreetNumber = suffixMatch[1];
            preservedSuffix = suffixMatch[2];
          }
        }
        
        // Third, try to extract from the user's typed value ref
        if (!preservedStreet && userTypedValueRef.current) {
          // Extract the full street portion (e.g., "34a Raimonde Road")
          const userTypedParts = userTypedValueRef.current.split(',');
          const fullStreet = userTypedParts[0]?.trim() || '';
          
          // Check if the street has a suffix pattern (e.g., "34a")
          const suffixPattern = fullStreet.match(/^(\d+)([a-zA-Z]+)\s/);
          if (suffixPattern) {
            preservedStreet = fullStreet;
            userStreetNumber = suffixPattern[1];
            preservedSuffix = suffixPattern[2];
          } else {
            // Also try just the number pattern at the start (for partial input)
            const numberMatch = userTypedValueRef.current.match(/^(\d+)([a-zA-Z]+)\s/);
            if (numberMatch) {
              userStreetNumber = numberMatch[1];
              preservedSuffix = numberMatch[2];
            }
          }
        }

        // Parse address components
        const components: any = {};
        place.address_components.forEach((component) => {
          const types = component.types;
          
          if (types.includes('street_number')) {
            components.streetNumber = component.long_name;
          }
          if (types.includes('subpremise')) {
            // Handle unit/apartment numbers like "a", "b", etc. after street number
            components.subpremise = component.long_name;
          }
          if (types.includes('route')) {
            components.route = component.long_name;
          }
          if (types.includes('locality')) {
            components.city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            components.state = component.short_name; // e.g., NSW, VIC
          }
          if (types.includes('country')) {
            components.country = component.long_name;
          }
          if (types.includes('postal_code')) {
            components.postalCode = component.long_name;
          }
          // Handle sublocality for suburbs
          if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
            components.suburb = component.long_name;
          }
        });

        // Build street address
        // CRITICAL RULE: When a place is selected from Google, ALWAYS use formatted_address
        // Manual input is ONLY for searching/lookup, NEVER for saving to DB
        // Only exception: preserve user-typed suffixes (e.g., "34a" in "34a Raimonde Road")
        let street = '';
        
        // Extract street from Google's formatted address
        const getFormattedStreet = () => {
          if (place.formatted_address) {
            const formattedParts = place.formatted_address.split(',');
            if (formattedParts.length > 0) {
              return formattedParts[0].trim();
            }
          }
          return null;
        };
        
        const formattedStreet = getFormattedStreet();
        
        // FIRST PRIORITY: If user typed a suffix (e.g., "34a"), preserve it in formatted address
        // This is the ONLY case where we modify Google's formatted address
        if (capturedStreetWithSuffixRef.current && formattedStreet) {
          const suffixMatch = capturedStreetWithSuffixRef.current.match(/(\d+)([a-zA-Z]+)/);
          if (suffixMatch) {
            const [, num, suffix] = suffixMatch;
            // Try to inject suffix into formatted street if it starts with the number
            if (formattedStreet.match(new RegExp(`^${num}\\b`))) {
              street = formattedStreet.replace(new RegExp(`^${num}\\b`), num + suffix);
            } else {
              // Can't inject, use formatted as-is
              street = formattedStreet;
            }
          } else {
            street = formattedStreet;
          }
        }
        // SECOND PRIORITY: ALWAYS use Google's formatted address when a place is selected
        // This ensures "180 midson" becomes "180 Midson Road" when selected from Google
        else if (formattedStreet) {
          street = formattedStreet;
        }
        // THIRD PRIORITY: Build from components (fallback if no formatted address)
        // This should rarely happen, but handles edge cases
        else {
          street = [components.streetNumber, components.subpremise, components.route]
            .filter(Boolean)
            .join(' ');
        }

        // Use suburb if city is not available (common in Australia)
        const city = components.city || components.suburb || '';

        // Get geographical coordinates
        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();

        const addressComponents: AddressComponents = {
          street: street || '',
          city,
          state: components.state || '',
          country: components.country || 'Australia',
          postalCode: components.postalCode || '',
          formattedAddress: place.formatted_address,
          latitude,
          longitude,
          placeId: place.place_id,
        };

        onChange(addressComponents);
        
        // Update input value with formatted address
        if (onInputChange && place.formatted_address) {
          onInputChange(place.formatted_address);
        }
      });

      autocompleteRef.current = autocompleteInstance;
    };

    setLoading(true);

    loadGoogleMaps(apiKey, ['places'])
      .then(() => {
        initAutocomplete();
      })
      .catch((error) => {
        console.error('Error loading Google Maps:', error);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      // Clean up event listeners
      if (inputRef.current && inputHandlerRef.current) {
        inputRef.current.removeEventListener('input', inputHandlerRef.current);
        inputHandlerRef.current = undefined;
      }
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        label={label}
        value={value}
        onChange={(e) => {
          // Track user's typed input to preserve suffixes like "a" in "34a"
          const typedValue = e.target.value;
          
          // ALWAYS store what the user types - this is critical for preserving suffixes
          userTypedValueRef.current = typedValue;
          
          // Extract and store street number with suffix if present (e.g., "34a")
          // Try multiple patterns to catch various input styles
          const patterns = [
            /^(\d+[a-zA-Z])\s/,           // "34a Raimonde"
            /^(\d+[a-zA-Z]),/,            // "34a,"
            /^(\d+[a-zA-Z])$/,            // just "34a" (when typing)
          ];
          
          for (const pattern of patterns) {
            const match = typedValue.match(pattern);
            if (match) {
              const streetNum = match[1];
              // Store if it has a suffix (contains a letter after digits)
              if (/\d[a-zA-Z]/.test(streetNum)) {
                lastStreetNumberWithSuffixRef.current = streetNum;
                break;
              }
            }
          }
          
          onInputChange && onInputChange(typedValue);
        }}
        fullWidth
        required={required}
        disabled={disabled || loading}
        error={error}
        helperText={helperText || (loading ? 'Loading address autocomplete...' : 'Start typing to search for an Australian address')}
        InputProps={{
          endAdornment: loading ? <CircularProgress size={20} /> : null,
        }}
      />
    </Box>
  );
};

